import { useEffect } from 'react';
import { Alert, App, Button, Card, Form, InputNumber, Space, Table, Tag, Typography } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../api/client';
import { Can } from '../../rbac/RequirePermission';
import { OnlineEarningCard, useOnlineEarningConfig } from './OnlineEarningCard';
import { TextChatPricingCard } from './TextChatPricingCard';

import {
  EDITABLE_CALL_TYPES,
  formValuesFromConfig,
  patchBodyFromForm,
  type CallPricingConfig,
  type CallType,
  type FormShape,
} from './callPricingForm';

/**
 * The rows: one per call type, for the one tier the product actually assigns.
 *
 * `coach` and `clinical` exist in the enum and keep their own server-side keys,
 * but listener approval hardcodes `peer` (see `ListenersPage`), so there is no
 * way to reach their pricing — showing empty cells for unreachable tiers invites
 * an operator to tune numbers that can never apply. Their keys keep serving
 * historical rows; this page simply never writes them.
 */
const TIER: keyof CallPricingConfig = 'peer';
const LABELS: Record<CallType, { label: string; hint: string }> = {
  direct: { label: 'Direct call', hint: 'Caller picked this listener' },
  random: { label: 'Random call', hint: 'Quick Connect match' },
};
// Only the direct row: random calls are retired (a direct call falls back to
// the next buddy at the direct price). Stored random values are not touched.
const ROWS = EDITABLE_CALL_TYPES.map((key) => ({ key, ...LABELS[key] }));

/**
 * Call pricing (admin/super-admin).
 *
 * Priced on three axes — tier × call type (direct|random) × mode (video|audio) —
 * giving four independent configurations per tier. The caller's price and the
 * listener's earning are separate numbers in separate units: changing one never
 * moves the other. Applies within ~60s (config cache).
 */
export function CallPricingPage() {
  const { message } = App.useApp();
  const qc = useQueryClient();
  const [form] = Form.useForm<FormShape>();

  // While daily online-time stages are ON, peer listeners earn the STAGE rate;
  // the "listener earns" cells below are then only the fallback. The caller
  // columns are always live.
  const stagesOn = useOnlineEarningConfig().data?.enabled === true;
  const earnTitle = (mode: string) =>
    stagesOn ? (
      <span>
        {mode} — listener earns (₹/min){' '}
        <Tag color="orange" style={{ marginLeft: 4 }}>
          fallback · stages ON
        </Tag>
      </span>
    ) : (
      `${mode} — listener earns (₹/min)`
    );

  const cfg = useQuery({
    queryKey: ['admin', 'call-pricing'],
    queryFn: async () => (await api.get<CallPricingConfig>('/admin/call-pricing')).data,
  });

  useEffect(() => {
    if (!cfg.data) return;
    form.setFieldsValue(formValuesFromConfig(cfg.data, TIER));
  }, [cfg.data, form]);

  const save = useMutation({
    mutationFn: async (v: FormShape) => {
      const body = patchBodyFromForm(v, TIER);
      return (await api.patch<CallPricingConfig>('/admin/call-pricing', body)).data;
    },
    onSuccess: () => {
      message.success('Call pricing saved — applies within ~60s.');
      void qc.invalidateQueries({ queryKey: ['admin', 'call-pricing'] });
    },
    onError: (e: unknown) =>
      message.error(
        (e as { response?: { data?: { message?: string } } }).response?.data?.message ??
          'Save failed',
      ),
  });

  // Caller price, in COINS/min. Deliberately no ₹ equivalent underneath: the
  // caller's price is a coin number, and putting a rupee figure beside the
  // listener's rupee earning invites subtracting one from the other, which is
  // not how either side is calculated any more.
  const coinsCell = (field: string) => (
    <Form.Item name={field} rules={[{ required: true }]} style={{ margin: 0 }}>
      <InputNumber
        min={0}
        max={100000}
        step={5}
        precision={0}
        suffix="coins"
        style={{ width: 130 }}
      />
    </Form.Item>
  );

  // Listener earning, in RUPEES/min — the real money credited to the listener.
  // Independent of the caller's coin price: no cross-check between the two.
  const rupeeCell = (field: string) => (
    <Form.Item name={field} rules={[{ required: true }]} style={{ margin: 0 }}>
      <InputNumber min={0} max={10000} step={0.5} precision={2} prefix="₹" style={{ width: 120 }} />
    </Form.Item>
  );

  const columns = [
    {
      title: 'Call type',
      key: 'label',
      width: 200,
      fixed: 'left' as const,
      render: (_: unknown, r: (typeof ROWS)[number]) => (
        <div>
          <Typography.Text strong>{r.label}</Typography.Text>
          <br />
          <Typography.Text type="secondary" style={{ fontSize: 11 }}>
            {r.hint}
          </Typography.Text>
        </div>
      ),
    },
    {
      title: 'Video — caller pays (coins/min)',
      key: 'pv',
      render: (_: unknown, r: { key: string }) => coinsCell(`${r.key}_pv`),
    },
    {
      title: earnTitle('Video'),
      key: 'ev',
      render: (_: unknown, r: { key: string }) => rupeeCell(`${r.key}_ev`),
    },
    {
      title: 'Audio — caller pays (coins/min)',
      key: 'pa',
      render: (_: unknown, r: { key: string }) => coinsCell(`${r.key}_pa`),
    },
    {
      title: earnTitle('Audio'),
      key: 'ea',
      render: (_: unknown, r: { key: string }) => rupeeCell(`${r.key}_ea`),
    },
  ];

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Typography.Title level={3} style={{ color: 'white', margin: 0 }}>
        Call pricing
      </Typography.Title>
      <Card loading={cfg.isLoading}>
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message={`Direct calls, priced separately for video and audio. When the buddy a caller picked can't take the call, it falls back to the next buddy on the same tier at this same price (random calls / Quick Connect are retired and no longer priced here). CALLER PAYS is in coins/min — the unit the caller sees in-app. LISTENER EARNS is in ₹/min — real money credited to the listener. The two are set independently: changing what the caller pays does not change what the listener earns, and neither is calculated from the other. Applies within ~60s.`}
        />
        <Form<FormShape> form={form} layout="vertical" onFinish={(v) => save.mutate(v)}>
          {stagesOn && (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 12 }}
              message="Listener daily online-time earnings are ON. Peer listeners earn the stage rates set below; the listener-earns columns here only apply if stages are switched off or cannot be used, and to non-peer tiers. What the CALLER pays always comes from this table."
            />
          )}
          <Table
            rowKey="key"
            size="small"
            pagination={false}
            dataSource={ROWS}
            columns={columns}
            scroll={{ x: 1000 }}
            style={{ marginBottom: 16 }}
          />
          <Can permission="call_pricing.edit">
            <Button type="primary" htmlType="submit" loading={save.isPending}>
              Save call pricing
            </Button>
          </Can>
        </Form>
      </Card>
      <TextChatPricingCard />
      <OnlineEarningCard />
    </Space>
  );
}
